import React, { forwardRef } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import Map from "./Map"; // Basiskomponente mit Mapbox
import mapboxgl from "mapbox-gl";
import { decimalFormatter } from "../../Utils/Formatter";
import moment from "moment";
import { renderToString } from "react-dom/server";

const Heatmap = forwardRef(({ onLoad, start, end, selectedOsSystem }, ref) => {
  const osSystemOptions = [
    { os: "ios", color: "#A87BCC" },
    { os: "android", color: "#9FCC7B" },
    { os: "win64", color: "#064CC4" },
    { os: "macos", color: "#3b82f6" },
    { os: "linux", color: "#06b6d4" },
    { os: "switch", color: "#F74251" },
  ];

  const handleMapLoad = () => {
    // 1) Neue GeoJSON-Quelle "heatmap"
    ref.current.addSource("heatmap", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });

    // 2) Fill-Layer für die Polygone
    ref.current.addLayer({
      id: "heatmap-fill",
      type: "fill",
      source: "heatmap",
      paint: {
        // Nach Dichte (Treffer pro Quadratgrad) faerben, nicht nach der
        // absoluten Summe: Der Server liefert beim Herauszoomen groebere
        // Zellen, deren Summen entsprechend hoeher liegen - sonst waere die
        // Weltansicht durchgehend dunkelrot. Bei cellSize 1 ist density gleich
        // hit_count, die Schwellen behalten dort ihre bisherige Bedeutung.
        "fill-color": [
          "interpolate",
          ["linear"],
          ["get", "density"],
          0,
          "rgba(33,102,172,0)",
          10,
          "rgb(103,169,207)",
          50,
          "rgb(209,229,240)",
          100,
          "rgb(253,219,199)",
          500,
          "rgb(239,138,98)",
          1000,
          "rgb(178,24,43)",
          5000,
          "rgb(103,0,31)",
          10000,
          "rgb(26,0,0)",
        ],
        "fill-opacity": 0.2,
      },
    });

    // -----------------------------------------
    // (B) Popup bei Klick auf ein Polygon
    ref.current.on("click", "heatmap-fill", (e) => {
      const { hit_count, total_length, os_data } = e.features[0].properties;
      // An der Klick-Position popup einblenden
      if (os_data) {
        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(
            renderToString(
              <>
                <p className="text-xs font-bold mb-2">
                  {moment(start).format("YYYY.MM.DD")} -{" "}
                  {moment(end).format("YYYY.MM.DD")}
                </p>
                <div className="grid grid-cols-3 text-xs text-gray-500">
                  <p className=" "></p>
                  <p className=" text-right px-2">#Flights</p>
                  <p className=" text-right">Distance</p>
                </div>
                {osSystemOptions.map((os) => {
                  const osData = JSON.parse(os_data)?.filter((d) => {
                    return d.os_system === os.os;
                  });
                  return (
                    <div
                      key={os.os}
                      className="grid grid-cols-3 text-xs"
                      style={{ color: os.color }}
                    >
                      <p className=" border-t border-gray-200">{os.os}</p>
                      <p className=" border-t border-gray-200 text-right px-2">
                        {decimalFormatter.format(
                          osData?.reduce(
                            (acc, curr) => acc + curr.hit_count,
                            0
                          ) ?? 0
                        )}
                      </p>
                      <p className=" border-t border-gray-200 text-right whitespace-nowrap">
                        {decimalFormatter.format(
                          osData?.reduce(
                            (acc, curr) => acc + curr.total_length,
                            0
                          )
                        ) + " "}
                        km
                      </p>
                    </div>
                  );
                })}

                <div className="grid grid-cols-3 text-xs mt-1 ">
                  <p className=" border-t border-gray-400 font-bold ">
                    {"total"}
                  </p>
                  <p className=" border-t border-gray-400 font-bold text-right px-2">
                    {decimalFormatter.format(
                      JSON.parse(os_data).reduce(
                        (acc, curr) => acc + curr.hit_count,
                        0
                      )
                    )}
                  </p>
                  <p className=" border-t border-gray-400 text-right font-bold whitespace-nowrap">
                    {decimalFormatter.format(
                      JSON.parse(os_data).reduce(
                        (acc, curr) => acc + curr.total_length,
                        0
                      )
                    ) + " km"}
                  </p>
                </div>
              </>
            )
          )
          .addTo(ref.current);
      } else {
        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(
            renderToString(
              <div key={selectedOsSystem}>
                <p className="text-xs font-bold mb-2">
                  {moment(start).format("YYYY.MM.DD")} -{" "}
                  {moment(end).format("YYYY.MM.DD")}
                </p>
                <div className="grid grid-cols-3 text-xs text-gray-500">
                  <p className=" "></p>
                  <p className=" text-right px-2">#Flights</p>
                  <p className=" text-right">Distance</p>
                </div>
                {
                  <div
                    className="grid grid-cols-3 text-xs"
                    style={{
                      color: osSystemOptions.find(
                        (o) => o.os === selectedOsSystem
                      )?.color,
                    }}
                  >
                    <p className=" border-t border-gray-200">
                      {selectedOsSystem}
                    </p>
                    <p className=" border-t border-gray-200 text-right px-2">
                      {decimalFormatter.format(hit_count ?? 0)}
                    </p>
                    <p className=" border-t border-gray-200 text-right whitespace-nowrap">
                      {total_length
                        ? decimalFormatter.format(Math.round(total_length))
                        : 0}{" "}
                      km
                    </p>
                  </div>
                }
              </div>
            )
          )
          .addTo(ref.current);
      }
    });
    onLoad(); // Signal an Elternkomponente: Map ist geladen
  };

  return <Map onLoad={handleMapLoad} ref={ref} useDarkMode />;
});

export default Heatmap;
